/**
 * Directory Modal Handler
 * Handles the complete stylist directory modal with search and pagination
 */

;(() => {
  'use strict'

  const ITEMS_PER_PAGE = 50
  const SEARCH_DEBOUNCE_MS = 300

  // Location pin shown beside a stylist's parsed state/province (when known).
  const LOCATION_PIN_SVG =
    '<svg class="stylist-locator__directory-card-location-icon" width="9" height="11" viewBox="0 0 20 25" fill="none" aria-hidden="true"><path d="M10 0C7.36507 0 4.83657 1.0397 2.96405 2.89348C1.09154 4.74726 0.0263493 7.26518 0 9.89997C0 16.7499 8.8125 24.3749 9.1875 24.6999C9.41392 24.8936 9.70206 25 10 25C10.2979 25 10.5861 24.8936 10.8125 24.6999C11.25 24.3749 20 16.7499 20 9.89997C19.9736 7.26518 18.9085 4.74726 17.0359 2.89348C15.1634 1.0397 12.6349 0 10 0ZM10 13.75C9.13471 13.75 8.28885 13.4934 7.56938 13.0126C6.84991 12.5319 6.28916 11.8486 5.95803 11.0492C5.62689 10.2498 5.54025 9.37012 5.70906 8.52145C5.87787 7.67279 6.29455 6.89324 6.90641 6.28139C7.51826 5.66954 8.29781 5.25286 9.14648 5.08405C9.99515 4.91524 10.8748 5.00188 11.6742 5.33301C12.4737 5.66414 13.1569 6.2249 13.6377 6.94436C14.1184 7.66382 14.375 8.50968 14.375 9.37497C14.375 10.5353 13.9141 11.6481 13.0936 12.4686C12.2731 13.289 11.1603 13.75 10 13.75Z" fill="currentColor"/></svg>'

  class DirectoryModal {
    constructor(container) {
      this.container = container
      this.apiBaseDomain = container.dataset.apiEndpoint || ''
      this.modal = container.querySelector('[data-element="directory-modal"]')
      this.openButtons = container.querySelectorAll('[data-element="directory-modal-open"]')
      this.closeButton = container.querySelector('[data-element="directory-modal-close"]')
      this.closeButtonDesktop = container.querySelector('[data-element="directory-modal-close-desktop"]')
      this.searchInput = container.querySelector('[data-element="directory-search-input"]')
      this.listContainer = container.querySelector('[data-element="directory-list"]')
      this.loadingElement = container.querySelector('[data-element="directory-loading"]')
      this.emptyElement = container.querySelector('[data-element="directory-empty"]')
      this.paginationContainer = container.querySelector('[data-element="directory-pagination"]')
      this.prevButton = container.querySelector('[data-element="directory-prev"]')
      this.nextButton = container.querySelector('[data-element="directory-next"]')
      this.pageInfo = container.querySelector('[data-element="directory-page-info"]')

      this.state = {
        isOpen: false,
        isLoading: false,
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        searchQuery: '',
        stylists: [],
        scrollY: 0,
      }

      this.handle = this.modal?.querySelector('[data-element="directory-modal-handle"]')
      this.searchTimeout = null
      this.boundKeydown = null
      this.abortController = null

      this.init()
    }

    init() {
      if (!this.modal) return
      this.bindEvents()
    }

    bindEvents() {
      // Open modal
      this.openButtons.forEach((button) => {
        button.addEventListener('click', () => this.open())
      })

      // Close modal via buttons
      ;[this.closeButton, this.closeButtonDesktop].forEach((target) => {
        if (!target) return
        target.addEventListener('click', () => this.close())
      })

      // Click outside to close (on the modal backdrop)
      this.modal?.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close()
        }
      })

      // Drag anywhere on the sheet to dismiss (app-style bottom sheet)
      const sheetContent = this.modal?.querySelector('.stylist-locator__directory-modal-content')
      const { attachSheetDrag } = globalThis.StylistLocator?.utils || {}
      if (sheetContent && attachSheetDrag) {
        attachSheetDrag(sheetContent, () => this.close())
      }

      // Search input
      if (this.searchInput) {
        this.searchInput.addEventListener('input', (e) => {
          this.handleSearch(e.target.value)
        })
        
        // Also handle Enter key for immediate search
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            // Clear debounce and search immediately
            if (this.searchTimeout) {
              clearTimeout(this.searchTimeout)
            }
            const query = e.target.value || ''
            this.state.searchQuery = query.trim()
            this.state.currentPage = 1
            this.fetchStylists(1, query.trim())
          }
        })
      }

      // Pagination buttons
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => {
          if (this.state.currentPage > 1) {
            this.goToPage(this.state.currentPage - 1)
          }
        })
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          if (this.state.currentPage < this.state.totalPages) {
            this.goToPage(this.state.currentPage + 1)
          }
        })
      }

      // Escape key to close
      this.boundKeydown = (e) => {
        if (e.key === 'Escape' && this.state.isOpen) {
          this.close()
        }
      }
      document.addEventListener('keydown', this.boundKeydown)
    }

    buildApiUrl(page = 1, search = '') {
      if (!this.apiBaseDomain) {
        console.warn('DirectoryModal: apiBaseDomain is empty')
        return null
      }
      
      try {
        const base = this.apiBaseDomain.replace(/\/$/, '')
        const url = new URL(`${base}/api/stylists`)
        url.searchParams.set('page', page.toString())
        url.searchParams.set('limit', ITEMS_PER_PAGE.toString())
        url.searchParams.set('sort', 'name')
        if (search && search.trim()) {
          url.searchParams.set('search', search.trim())
        }
        return url.toString()
      } catch (error) {
        // Fallback: construct URL string manually if URL constructor fails
        console.warn('DirectoryModal: URL constructor failed, using fallback', error)
        const base = this.apiBaseDomain.replace(/\/$/, '')
        let urlStr = `${base}/api/stylists?page=${page}&limit=${ITEMS_PER_PAGE}&sort=name`
        if (search && search.trim()) {
          urlStr += `&search=${encodeURIComponent(search.trim())}`
        }
        return urlStr
      }
    }

    async fetchStylists(page = 1, search = '') {
      const url = this.buildApiUrl(page, search)
      if (!url) {
        console.warn('DirectoryModal: API endpoint not configured')
        this.setLoading(false)
        return
      }

      // Cancel any pending request
      if (this.abortController) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()

      this.setLoading(true)

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
          },
          signal: this.abortController.signal,
        })

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`)
        }

        const data = await response.json()

        this.state.stylists = Array.isArray(data.stylists) ? data.stylists : []
        this.state.currentPage = data.pagination?.page || 1
        this.state.totalPages = data.pagination?.total_pages || 1
        this.state.totalCount = data.pagination?.total_count || 0

        this.render()
      } catch (error) {
        if (error.name === 'AbortError') {
          // Request was cancelled, ignore
          return
        }
        console.error('DirectoryModal: Failed to fetch stylists', error)
        this.state.stylists = []
        this.state.totalPages = 1
        this.state.totalCount = 0
        this.render()
      } finally {
        this.setLoading(false)
        this.abortController = null
      }
    }

    handleSearch(query) {
      // Clear any existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      const searchValue = query || ''

      // Debounce the search
      this.searchTimeout = setTimeout(() => {
        const trimmedQuery = searchValue.trim()
        this.state.searchQuery = trimmedQuery
        this.state.currentPage = 1 // Reset to first page on new search
        this.fetchStylists(1, trimmedQuery)
      }, SEARCH_DEBOUNCE_MS)
    }

    goToPage(page) {
      if (page < 1 || page > this.state.totalPages) return
      this.state.currentPage = page
      this.fetchStylists(page, this.state.searchQuery)

      // Scroll list to top
      if (this.listContainer) {
        this.listContainer.scrollTop = 0
      }
    }

    setLoading(isLoading) {
      this.state.isLoading = isLoading

      if (this.loadingElement) {
        this.loadingElement.style.display = isLoading ? 'flex' : 'none'
      }

      if (this.listContainer) {
        this.listContainer.style.opacity = isLoading ? '0.5' : '1'
      }
    }

    render() {
      this.renderList()
      this.renderPagination()
    }

    renderList() {
      if (!this.listContainer) return

      const { stylists } = this.state

      // Show empty state if no results
      if (this.emptyElement) {
        this.emptyElement.style.display = stylists.length === 0 && !this.state.isLoading ? 'flex' : 'none'
      }

      if (stylists.length === 0) {
        this.listContainer.innerHTML = ''
        return
      }

      const html = stylists.map((stylist) => this.renderStylistCard(stylist)).join('')
      this.listContainer.innerHTML = html
    }

    renderStylistCard(stylist) {
      const name = this.escapeHtml(stylist.name || 'Unknown Stylist')
      const role = this.escapeHtml(stylist.role || 'IBE® Stylist')
      const profilePicture = stylist.profile_picture || ''
      const initials = this.getInitials(stylist.name || '')
      const location = this.escapeHtml(stylist.location || '')

      const avatarContent = profilePicture
        ? `<img src="${this.escapeHtml(profilePicture)}" alt="${name}" loading="lazy">`
        : `<span class="stylist-locator__directory-card-initials">${initials}</span>`

      const locationMarkup = location
        ? `<span class="stylist-locator__directory-card-location">${LOCATION_PIN_SVG}<span>${location}</span></span>`
        : ''

      return `
        <div class="stylist-locator__directory-card">
          <div class="stylist-locator__directory-card-avatar">
            ${avatarContent}
          </div>
          <div class="stylist-locator__directory-card-info">
            <span class="stylist-locator__directory-card-role">${role}</span>
            <span class="stylist-locator__directory-card-name">${name}</span>
            ${locationMarkup}
          </div>
        </div>
      `
    }

    renderPagination() {
      if (!this.paginationContainer) return

      const { currentPage, totalPages, totalCount } = this.state

      // Hide pagination if only one page or no results
      if (totalPages <= 1 || totalCount === 0) {
        this.paginationContainer.style.display = 'none'
        return
      }

      this.paginationContainer.style.display = 'flex'

      // Update page info
      if (this.pageInfo) {
        this.pageInfo.textContent = `Page ${currentPage} of ${totalPages}`
      }

      // Update button states
      if (this.prevButton) {
        this.prevButton.disabled = currentPage <= 1
      }

      if (this.nextButton) {
        this.nextButton.disabled = currentPage >= totalPages
      }
    }

    open() {
      if (this.state.isOpen) return

      // Close any other open drawers first
      const controller = this.container._stylistLocatorController
      if (controller) controller._closeAllDrawers()

      this.state.isOpen = true
      this.lockBodyScroll()

      // Add open class with animation delay
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal?.classList.add('stylist-locator__directory-modal--open')
        })
      })

      document.body.classList.add('stylist-locator__directory-modal-open')

      // Reset state and fetch initial data
      this.state.currentPage = 1
      this.state.searchQuery = ''
      if (this.searchInput) {
        this.searchInput.value = ''
      }

      this.fetchStylists(1, '')

      // Focus the search input
      setTimeout(() => {
        this.searchInput?.focus()
      }, 100)
    }

    close() {
      if (!this.state.isOpen) return

      this.state.isOpen = false
      this.modal?.classList.remove('stylist-locator__directory-modal--open')
      document.body.classList.remove('stylist-locator__directory-modal-open')

      this.unlockBodyScroll()

      // Cancel any pending requests
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }

      // Clear search timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = null
      }

      // Return focus to the button that opened the modal
      if (this.openButtons.length > 0) {
        this.openButtons[0].focus()
      }
    }

    lockBodyScroll() {
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
    }

    unlockBodyScroll() {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }

    getInitials(name) {
      if (!name) return ''
      const parts = name.trim().split(/\s+/)
      if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase()
      }
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    }

    escapeHtml(text) {
      if (!text) return ''
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    }

    destroy() {
      if (this.boundKeydown) {
        document.removeEventListener('keydown', this.boundKeydown)
        this.boundKeydown = null
      }

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = null
      }

      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
    }
  }

  // Initialize when DOM is ready
  function initDirectoryModal() {
    const containers = document.querySelectorAll('.js-stylist-locator')
    containers.forEach((container) => {
      // Check if already initialized
      if (container._directoryModal) return
      container._directoryModal = new DirectoryModal(container)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDirectoryModal)
  } else {
    initDirectoryModal()
  }

  // Handle Shopify section reload
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target?.querySelector('.js-stylist-locator')
    if (container && !container._directoryModal) {
      container._directoryModal = new DirectoryModal(container)
    }
  })

  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target?.querySelector('.js-stylist-locator')
    if (container && container._directoryModal) {
      container._directoryModal.destroy()
      container._directoryModal = null
    }
  })
})()
